export enum CustomerLabelEnum {
  E_FENCE = 'EFence',
  IN_HOME = 'In-Home',
  VIRTUAL = 'Virtual',
}

export enum CountryMapEnum {
  US = 'United States',
  CA = 'Canada',
}

export type ScheduleStage =
  | 'personalDetails'
  | 'dealerSelect'
  | 'appointmentSelect'
  | 'confirmation';

export interface AppointmentDataProps {
  workTypes: {
    customerLabel: string;
    appointmentSlots: SlotProps[];
    id?: string;
    isDefaultWorkType: boolean;
  }[];
}

export interface ConfirmationProps {
  appointmentData?: AppointmentDataProps;
  dealer?: DealerProps;
  confirmedAppointmentData?: SchedulerState['confirmedAppointmentData'];
  confirmationScenario:
    | 'existingContact'
    | 'isEmailOrCrmDealer'
    | 'noDealersAvailable'
    | 'noAppointmentsAvailable'
    | 'failedAppointmentSchedule'
    | 'appointmentConfirmedVirtual'
    | 'appointmentConfirmedInHome'
    | 'remoteConsultationConfirmed';
  handleRemoteConsultation?: () => void;
}

export interface PersonalDetailsProps {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  stateAbbr: string;
  postalCode: string;
  country: string;
  referredByCode: string;
  loading?: boolean;
  error?: string | null;
  personalSubmitButtonLabel?: string | null;
  handlePersonalDetailsChange?: (fieldName: string, value: string) => void;
  handlePersonalInfoSubmit?: (recaptchaToken: string) => void;
}

export interface DealerProps {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  mainServiceArea: string;
  serviceAreas?: string;
  subdomain?: string;
  isCod?: boolean;
  isCrm?: boolean;
  isEFence?: boolean;
  isEmail?: boolean;
  dealerType?: string;
  isOnlineScheduling?: boolean;
  isInHomeConsult?: boolean;
  isVirtualConsult?: boolean;
  dealerWebsite?: string;
  workTypes?: string[];
  cta?: string | null;
}
export interface SchedulerState extends PersonalDetailsProps {
  dealers: DealerProps[];
  selectedDealer: DealerProps | null;
  loading: boolean;
  loadingMessage: string;
  error: string | null;
  appointmentData: AppointmentDataProps;
  appointmentDays: { rawDate: string; formattedDate: string }[];
  appointmentTimes: { slotDisplay: string; start: string }[];
  appointmentNotAvailable: boolean;
  workType: string;
  stage: ScheduleStage;
  confirmationScenario: ConfirmationProps['confirmationScenario'];
  confirmedAppointmentData?: {
    appointmentId: string;
    contactId: string;
    slotDisplay: string;
    slotDateFormatted: string;
    arrivalWindowStart: string;
    arrivalWindowEnd: string;
    isExactAppointment: boolean;
    workTypeId: string;
    workTypeName: string;
    estimatedAppointmentLength: string;
  };
  sessionId?: string;
  selectedSlot: SlotProps;
}

export interface SlotProps {
  appointmentId: string;
  appointmentGrade: number;
  start: string;
  finish: string;
  startDate: string;
  endDate: string;
  slotDisplay: string;
  timeZone: string;
  workOrderId: string;
  earlierAppointmentDesired: boolean;
  exactAppointment: boolean;
  workTypeId: string;
  workTypeName: string;
}

export enum CustomerProcessStatusEnum {
  APPOINTMENTS_AVAILABLE = 1,
  EMAIL_SENT = 2,
  EXISTING_CONTACT = 3,
  NO_APPOINTMENTS_AVAILABLE = 4,
  NO_WORK_TYPES = 5,
}

export enum SchedulerActionType {
  SET_PERSONAL_DETAIL_FIELD = 'SET_PERSONAL_DETAIL_FIELD',
  SET_LOADING = 'SET_LOADING',
  SET_DEALER_FETCH_SUCCESS = 'SET_DEALER_FETCH_SUCCESS',
  SET_APPOINTMENTS = 'SET_APPOINTMENTS',
  SET_SELECTED_DEALER = 'SET_SELECTED_DEALER',
  SET_ERROR = 'SET_ERROR',
  SET_STAGE = 'SET_STAGE',
  SET_CONFIRMATION_SCENARIO = 'SET_CONFIRMATION_SCENARIO',
  SET_APPOINTMENT_DAYS = 'SET_APPOINTMENT_DAYS',
  SET_APPOINTMENT_TIMES = 'SET_APPOINTMENT_TIMES',
  SET_APPOINTMENT_DAYS_TIMES_AND_WORKTYPE = 'SET_APPOINTMENT_DAYS_TIMES_AND_WORKTYPE',
  SET_SELECTED_SLOT = 'SET_SELECTED_SLOT',
  SET_APPOINTMENT_NOT_AVAILABLE = 'SET_APPOINTMENT_NOT_AVAILABLE',
}

export interface SchedulerActionBase {
  type: SchedulerActionType;
}

export interface SetPersonalDetailFieldAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_PERSONAL_DETAIL_FIELD;
  field: keyof SchedulerState;
  value: string;
}

export interface SetLoadingAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_LOADING;
  loading: boolean;
  loadingMessage?: string;
}

export interface SetDealerFetchSuccessAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_DEALER_FETCH_SUCCESS;
  dealers: DealerProps[];
}

export interface SetAppointmentsAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_APPOINTMENTS;
  appointments: AppointmentDataProps;
}

export interface SetSelectedDealerAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_SELECTED_DEALER;
  dealer: DealerProps;
}

export interface SetErrorAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_ERROR;
  error: string;
}

export interface SetAppointmentNotAvailableAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_APPOINTMENT_NOT_AVAILABLE;
  appointmentNotAvailable: boolean;
}

export interface SetStageAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_STAGE;
  stage:
    | 'personalDetails'
    | 'dealerSelect'
    | 'appointmentSelect'
    | 'confirmation';
}

export interface SetSelectedSlotAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_SELECTED_SLOT;
  selectedSlot: SlotProps;
}

export interface SetConfirmationScenarioAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_CONFIRMATION_SCENARIO;
  confirmationScenario: ConfirmationProps['confirmationScenario'];
  confirmedAppointmentData?: SchedulerState['confirmedAppointmentData'];
}

export interface SetAppointmentDaysAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_APPOINTMENT_DAYS;
  appointmentDays: { rawDate: string; formattedDate: string }[];
}

export interface SetAppointmentTimesAction extends SchedulerActionBase {
  type: SchedulerActionType.SET_APPOINTMENT_TIMES;
  appointmentTimes: { slotDisplay: string; start: string }[];
}

export interface SetAppointmentDaysTimesAndWorkTypeAction
  extends SchedulerActionBase {
  type: SchedulerActionType.SET_APPOINTMENT_DAYS_TIMES_AND_WORKTYPE;
  appointmentDays: { rawDate: string; formattedDate: string }[];
  appointmentTimes: { slotDisplay: string; start: string }[];
  workType: string;
}

export type SchedulerAction =
  | SetPersonalDetailFieldAction
  | SetLoadingAction
  | SetDealerFetchSuccessAction
  | SetAppointmentsAction
  | SetSelectedDealerAction
  | SetErrorAction
  | SetStageAction
  | SetConfirmationScenarioAction
  | SetAppointmentDaysAction
  | SetAppointmentTimesAction
  | SetAppointmentDaysTimesAndWorkTypeAction
  | SetSelectedSlotAction
  | SetAppointmentNotAvailableAction;

export interface LeadCaptureDataLayerEventProps {
  event: string;
  source?: string;
  address_string?: string;
  appointment_unavailable?: string;
  dealer_type_company_owned?: string;
  dealer_type_crm?: string;
  dealer_type_email?: string;
  dealer_type_efence?: string;
  existing_customer?: string;
  no_dealer_available?: string;

  // Required for Enhanced Conversions (manual GTM setup)
  email?: string;
  'phone number'?: string;
  'Submitted First Name'?: string;
  'Submitted Last Name'?: string;
  'street address'?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  ConsultationScheduled?: boolean;

  // Optional nested structure from other events (e.g. form submit)
  prospect?: {
    email?: string;
    'street address'?: string;
    firstName?: string;
    lastName?: string;
    'phone number'?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
  };
}
